import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SafeAreaView, ScrollView, View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PlatformIcon as Icon } from '@cubemeet/ui';
import { supabase } from '@cubemeet/core/services/supabase';

// Simple web page to complete Supabase password recovery: user lands here via email link
// The library is configured with detectSessionInUrl=true so the recovery session should be set automatically
const ResetPasswordPage: React.FC = () => {
  const { translations } = useI18n();
  const t = useMemo(() => {
    return (key: string, params?: Record<string, any>): string => {
      if (!translations) return key;
      const value = key
        .split('.')
        .reduce<any>((acc, part) => (acc && typeof acc === 'object' ? acc[part] : null), translations);
      if (typeof value === 'string') {
        if (!params) return value;
        return value.replace(/\{(\w+)\}/g, (_, p) => String(params[p] ?? ''));
      }
      return key;
    };
  }, [translations]);

  const tr = useCallback(
    (key: string, fallback: string, params?: Record<string, any>): string => {
      const result = t(key, params);
      if (!result || result === key) return fallback;
      return result;
    },
    [t]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);

  useEffect(() => {
    // On mount, check if we have a recovery session; supabase picks it from URL hash
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        // We can't directly read event type here, but presence of session after recovery redirect is enough
        setHasRecoverySession(!!session);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [location.key]);

  const handleUpdatePassword = async () => {
    if (!password || password.length < 6) {
  setError(tr('auth.validation.passwordMinLength', '密码至少 6 位'));
      return;
    }
    if (password !== confirm) {
  setError(tr('auth.validation.passwordsMustMatch', '两次输入的密码不一致'));
      return;
    }
    setError(null);

    try {
      setLoading(true);
      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) throw error as any;
      setSuccess(true);
    } catch (err: any) {
      console.error('更新密碼失敗', err);
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <View style={styles.header}>
          <TouchableOpacity style={styles.back} onPress={() => navigate('/auth')}>
            <Icon name="arrow-back" size={22} color="#2563EB" />
            <Text style={styles.backText}>{tr('common.back', '返回')}</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.form}>
          <Text style={styles.title}>{tr('auth.resetPassword', '重置密码')}</Text>

          {loading ? (
            <Text style={{ color: '#6B7280' }}>{tr('common.loading', '加载中…')}</Text>
          ) : hasRecoverySession ? (
            success ? (
              <View style={styles.successBox}>
                <Text style={styles.successText}>{tr('auth.passwordUpdated', '密码已更新，现在可以使用新密码登录。')}</Text>
                <TouchableOpacity style={[styles.button, { marginTop: 16 }]} onPress={() => navigate('/auth')}>
                  <Text style={styles.buttonText}>{tr('auth.backToLogin', '返回登录')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <>
                <Text style={styles.label}>{tr('auth.newPassword', '新密码')}</Text>
                <TextInput
                  style={styles.input}
                  value={password}
                  onChangeText={setPassword}
                  placeholder={tr('auth.newPassword', '新密码')}
                  secureTextEntry
                />

                <Text style={styles.label}>{tr('auth.confirmPassword', '确认新密码')}</Text>
                <TextInput
                  style={styles.input}
                  value={confirm}
                  onChangeText={setConfirm}
                  placeholder={tr('auth.confirmPassword', '确认新密码')}
                  secureTextEntry
                />

                {error ? <Text style={styles.error}>{error}</Text> : null}

                <TouchableOpacity style={[styles.button, loading && styles.buttonDisabled]} onPress={handleUpdatePassword} disabled={loading}>
                  <Text style={styles.buttonText}>{tr('auth.updatePassword', '更新密码')}</Text>
                </TouchableOpacity>
              </>
            )
          ) : (
            <View style={styles.errorBox}>
              <Text style={styles.error}>{tr('auth.recoveryLinkInvalid', '恢复会话无效或已过期，请重新请求重置邮件。')}</Text>
              <TouchableOpacity style={[styles.button, { marginTop: 16 }]} onPress={() => navigate('/auth/forgot')}>
                <Text style={styles.buttonText}>{tr('auth.forgotPassword', '忘记密码？')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#f9f9f9' },
  content: { flexGrow: 1, padding: 20 },
  header: { height: 48 },
  back: { flexDirection: 'row', alignItems: 'center' },
  backText: { color: '#2563EB', marginLeft: 6 },
  form: { maxWidth: 540, alignSelf: 'center' },
  title: { fontSize: 24, fontWeight: '700', color: '#111827', marginBottom: 16 },
  label: { fontSize: 14, color: '#6B7280', marginTop: 12, marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#E5E7EB', paddingHorizontal: 12, paddingVertical: 10 },
  button: { marginTop: 20, backgroundColor: '#2563EB', paddingVertical: 12, borderRadius: 8, alignItems: 'center' },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { color: '#fff', fontWeight: '700' },
  error: { color: '#B91C1C' },
  errorBox: { backgroundColor: '#FEF2F2', borderColor: '#FECACA', borderWidth: 1, padding: 12, borderRadius: 8 },
  successBox: { backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', borderWidth: 1, padding: 12, borderRadius: 8 },
  successText: { color: '#065F46' },
});

export default ResetPasswordPage;
